import { useState } from 'react';
import type { FormEvent } from 'react';
import { workTypes } from '@/mocks/site';

const FORM_URL = 'https://readdy.ai/api/form/dbanshr9uihupc2pc6d0';
type Status = 'idle' | 'sending' | 'success' | 'error';

const field = 'w-full bg-transparent border-0 border-b border-foreground-400 focus:border-foreground-950 focus:outline-none focus:ring-0 py-3.5 text-[15px] user-invalid:border-accent-500 placeholder:text-foreground-400';
const label = 'block text-[11px] uppercase tracking-[0.2em] text-foreground-600';

export default function EstimateForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [count, setCount] = useState(0);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const hp = String(data.get('website_alt') ?? '').trim();
    if (hp) { setStatus('success'); return; }
    data.delete('website_alt');
    if (!form.checkValidity()) { form.reportValidity(); return; }
    if (String(data.get('details') ?? '').length > 500) {
      setStatus('error'); setFormError('Project details must be 500 characters or fewer.'); return;
    }
    setStatus('sending'); setFormError('');
    try {
      const body = new URLSearchParams();
      data.forEach((v, k) => body.append(k, String(v)));
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: unknown } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = String(parsed?.meta?.message || parsed?.message || (typeof parsed?.meta?.detail === 'string' ? parsed.meta.detail : '') || responseText || '');
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg)) {
        setStatus('success'); form.reset(); setCount(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Your request could not be sent. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error — your request was not sent. Please check your connection and try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="border-t border-foreground-950 pt-8" role="status">
        <h2 className="font-heading font-medium text-3xl tracking-tight">Request received.</h2>
        <p className="mt-3 text-[15px] text-foreground-700">Thank you. We will review your project details and reply by email.</p>
        <button type="button" onClick={() => setStatus('idle')} className="mt-6 text-[12px] uppercase tracking-[0.18em] border-b border-foreground-950 pb-0.5 cursor-pointer whitespace-nowrap">Send another request</button>
      </div>
    );
  }

  return (
    <form id="estimate-form" data-readdy-form onSubmit={onSubmit} noValidate className="flex flex-col gap-10 md:gap-12">
      <input type="text" name="website_alt" className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 md:gap-y-12">
        <div>
          <label htmlFor="f-name" className={label}>Name *</label>
          <input id="f-name" name="name" required autoComplete="name" className={field} />
        </div>
        <div>
          <label htmlFor="f-email" className={label}>Email *</label>
          <input id="f-email" name="email" type="email" required autoComplete="email" className={field} />
        </div>
        <div>
          <label htmlFor="f-zip" className={label}>ZIP / Postal Code *</label>
          <input id="f-zip" name="postal_code" required autoComplete="postal-code" maxLength={12} className={field} />
        </div>
        <div>
          <label htmlFor="f-type" className={label}>Type of Work *</label>
          <select id="f-type" name="work_type" required defaultValue="" className={`${field} cursor-pointer`}>
            <option value="" disabled>Select…</option>
            {workTypes.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="f-details" className={label}>Project Details *</label>
        <textarea id="f-details" name="details" required rows={5} maxLength={500} onChange={(e) => setCount(e.target.value.length)} placeholder="Wall type, current condition, approximate area, finish you have in mind…" className={`${field} resize-none mt-2 h-36 md:h-44 leading-relaxed`} />
        <span className={`block mt-2 text-right text-[11px] tracking-[0.12em] tabular-nums ${count >= 480 ? 'text-accent-700' : 'text-foreground-500'}`}>{count}/500</span>
      </div>
      {status === 'error' && <p role="alert" className="text-sm text-accent-700 border-l-2 border-accent-500 pl-3">{formError}</p>}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 border-t border-foreground-300/70 pt-8">
        <p className="text-[13px] leading-relaxed text-foreground-600 max-w-sm">Fields marked * are required. We review each request individually and reply by email. Service availability will be confirmed based on your project location.</p>
        <button type="submit" disabled={status === 'sending'} className="w-full sm:w-auto justify-center inline-flex items-center gap-3 px-7 py-4 rounded-[2px] bg-primary-500 text-background-50 hover:bg-accent-500 disabled:opacity-60 text-[12px] font-semibold uppercase tracking-[0.18em] whitespace-nowrap cursor-pointer transition-colors">
          {status === 'sending' ? 'Sending…' : 'Request an estimate'} <i className="ri-arrow-right-line" />
        </button>
      </div>
    </form>
  );
}